import {useCallback} from 'react';
import {
	EntityType,
	FormData,
	Repository,
} from '../types';
import {
	PROPERTY_DEFINITION_TYPES,
} from '../constants';
import {
	assignRandomId,
} from '../common/util';
import {entriesAddMany, selectAllEntries, selectLocationId} from '../containers/contentSlice';
import {useDispatch} from 'react-redux';
import {useAppSelector} from '../hooks';
import {useEntryCreateMutation} from '../services/records';
import {selectIsAuthenticated} from '../containers/authSlice';
import slugify from 'slugify';
import pluralize from 'pluralize';
import {
	formDataSetState,
	openAddForm,
	selectFormData,
	selectFormEntityTypeOptions,
	selectIsAddDialogOpen,
	selectAddFormRepositoryEntryType,
	closeAddForm,
} from '../containers/formDataSlice';
import {selectCurrentLocationEntriesMaxRank} from '../containers/repositoryEntryViewSlice';
import { selectRepository } from '../containers/repositoryViewSlice';
import { NOTE_REPOSITORY } from '../systemRecords/note';

const getAutofillValues = (entityType: EntityType, key: string, value: any): FormData => {
	if (PROPERTY_DEFINITION_TYPES.includes(entityType)) {
		if (key === 'label') {
			return {type: entityType, slug: slugify(value, {lower:true})};
		}
	} else if (entityType === EntityType.Repository) {
		return key === 'title' && pluralize.isPlural(value) ? {type: entityType, singular: pluralize.singular(value)} : {type: entityType};
	}
	return {type: entityType};
};

export const useAddEntryForm = () => {
	const dispatch = useDispatch();
	const parentId = useAppSelector(selectLocationId);
	const formData = useAppSelector(selectFormData);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const [entryCreateMutation] = useEntryCreateMutation();
	const addFormRepositoryOptions = useAppSelector(selectFormEntityTypeOptions);
	const currentMaxRank = useAppSelector(selectCurrentLocationEntriesMaxRank);
	const isAddDialogOpen = useAppSelector(selectIsAddDialogOpen);
	const allEntries = useAppSelector(selectAllEntries);
	const locationRepository = useAppSelector(selectRepository);
	const addFormRepositoryEntryType = useAppSelector(selectAddFormRepositoryEntryType);

	const openAddNoteDialog = useCallback(() => {
		const noteRepository = NOTE_REPOSITORY;
		dispatch(openAddForm({ repository: noteRepository as Repository }));
	}, [dispatch, addFormRepositoryOptions, allEntries, locationRepository]);

	const closeAddDialog = () => dispatch(closeAddForm());

	const handleAddDialogValueChange = useCallback((key: string, value: any) => {
		if (value !== ' ') {
			const autoFillData = getAutofillValues(addFormRepositoryEntryType as EntityType, key, value);
			dispatch(formDataSetState({
				...formData,
				...autoFillData,
				[key]: value,
			}));
		}
	}, [formData]);

	const onAddDialogSubmit = (payload: FormData) => {
		const newEntryPayload = assignRandomId({
			...payload,
			parentId: parentId ?? null,
			rank: currentMaxRank + 1
		});
		if (isAuthenticated) {
			entryCreateMutation([newEntryPayload]);
		} else {
			dispatch(entriesAddMany([newEntryPayload]));
		}
		closeAddDialog();
	};

	return {isAddDialogOpen, closeAddDialog, openAddNoteDialog, handleAddDialogValueChange, onAddDialogSubmit};
};
